import * as React from "react";
import { take, call } from "redux-saga/effects";
import { calcPrice } from "@grandvisionhq/analytics";
import { MediaQuery, EscapeHandlerStackProvider } from "@grandvisionhq/common";
import { Actions, Configurator } from "@grandvisionhq/configurator";
import { useSelector, useSaga, actions } from "@grandvisionhq/state";
import { UploadProvider } from "@grandvisionhq/upload";
import { RxGlassesExpanderContent } from "../../components/Footer/RxGlassesExpanderContent/components/Footer/RxGlassesExpanderContent/RxGlassesExpanderContent.js";
import { RxGlassesTopBar } from "../../components/Footer/RxGlassesTopBar/components/Footer/RxGlassesTopBar/RxGlassesTopBar.js";
import { RxGlassesConfiguratorProvider } from "../../context/context/RxGlassesConfiguratorContext.js";
import { useConfiguratorStepSubmit } from "../../hooks/hooks/use-configurator-step-submit.js";
import "../../state/state/actions.js";
import { PRESCRIPTION_FILE, NEW_PRESCRIPTION_AND_UPLOAD, RX_SCOPE, PD_SCOPE } from "../../state/state/constants.js";
import "../../state/add-to-cart/state/add-to-cart/selectors.js";
import "../../state/bundles/state/bundles/selectors.js";
import "../../state/bundles/state/bundles/reducer.js";
import { getSelectedLensType, getSelectedPackage, getSelectedAddOn, getPrescriptionType, getBundlePrice } from "../../state/configurator/state/configurator/selectors.js";
import "../../state/configurator/state/configurator/reducer.js";
import "../../state/products/state/products/selectors.js";
import "../../state/products/state/products/reducer.js";
import { listenForStepChanges } from "../../state/configurator/state/configurator/sagas.js";
import { stepsMap as stepsMap$1 } from "../../state/configurator/steps/rx-frames/state/configurator/steps/rx-frames/index.js";
import { stepsMap } from "../../state/configurator/steps/rx-sun/state/configurator/steps/rx-sun/index.js";
import { stepSequence } from "../../state/configurator/steps/shared/state/configurator/steps/shared/index.js";
import { pushDataLayerEvent } from "../../state/data-layer/state/data-layer/events.js";
import { listenForConfigurationChanges } from "../../state/data-layer/state/data-layer/sagas.js";
import { convertCmsRxEntries } from "../../utils/utils/convert-cms-rx-entries.js";
const scopes = [RX_SCOPE, PD_SCOPE];
const contentProps = {
  footerTopBarContent: /* @__PURE__ */ React.createElement(RxGlassesTopBar, null),
  footerExpanderContent: /* @__PURE__ */ React.createElement(RxGlassesExpanderContent, null),
  hideExpanderOnLastStep: true
};
const RxGlassesConfigurator = (props) => {
  const isAboveTabletPortrait = MediaQuery.useIsAbove(MediaQuery.BreakpointName.TABLET_PORTRAIT);
  const selectedLensType = useSelector(
    (s) => getSelectedLensType(s)
  );
  const selectedLensPackage = useSelector(
    (s) => getSelectedPackage(s)
  );
  const selectedLensAddOn = useSelector((s) => getSelectedAddOn(s));
  const prescriptionType = useSelector(
    (s) => getPrescriptionType(s)
  );
  const bundlePrice = useSelector(
    (s) => getBundlePrice(s, { id: props.id })
  );
  useSaga(
    React.useMemo(
      () => function* () {
        while (true) {
          yield take((a) => actions.isType(a, Actions.finishSuccess) && a.payload.id === props.id);
          if (props.onModalClose) {
            yield call(props.onModalClose);
          }
        }
      },
      [props.id, props.onModalClose]
    )
  );
  useSaga(
    React.useMemo(
      () => function* () {
        yield* listenForConfigurationChanges(props.id);
        yield* listenForStepChanges(props.id);
      },
      [props.id]
    )
  );
  const finishHandler = useConfiguratorStepSubmit(props.id);
  if (!props.isOpen) {
    return null;
  }
  const resolvedStepsMap = props.id === "rxSun" ? stepsMap : stepsMap$1;
  const rxConfigBundlePrice = bundlePrice?.discountedPrice ? calcPrice(bundlePrice?.discountedPrice, true) : calcPrice(bundlePrice?.originalPrice, true);
  const rxConfigData = {
    lens_type: selectedLensType?.title,
    lens_variant: selectedLensType?.colorCode,
    lens_package: selectedLensPackage?.title,
    "add-ons": selectedLensAddOn?.title,
    price: rxConfigBundlePrice,
    prescription_input_method: prescriptionType
  };
  const { entryTypes } = props.configuratorDocument?.prescriptionStep || {};
  const rxEntryTypes = convertCmsRxEntries(entryTypes);
  const shouldCheckUploadConfig = rxEntryTypes?.some(
    ({ key }) => [PRESCRIPTION_FILE, NEW_PRESCRIPTION_AND_UPLOAD].includes(key)
  );
  return /* @__PURE__ */ React.createElement(
    RxGlassesConfiguratorProvider,
    {
      frame: props.frame,
      frameSku: props.frameSku,
      frameSlug: props.frameSlug,
      opticianForm: props.opticianForm,
      configuratorDocument: props.configuratorDocument,
      rxEntryTypes,
      id: props.id
    },
    /* @__PURE__ */ React.createElement(UploadProvider, { scopes, shouldFetchConfigs: shouldCheckUploadConfig }, /* @__PURE__ */ React.createElement(EscapeHandlerStackProvider, null, /* @__PURE__ */ React.createElement(
      Configurator,
      {
        id: props.id,
        className: "glasses-configurator-modal",
        preventVerticalScroll: true,
        "data-t": "glasses-configurator-modal",
        stepSequence,
        stepsMap: resolvedStepsMap,
        lastStepCTALabelKey: "products.glasses.configurator.addToCart",
        isOpen: props.isOpen,
        onModalClose: props.onModalClose,
        onFinish: finishHandler,
        ...isAboveTabletPortrait ? {} : contentProps,
        onTriggerDataLayer: (dataInput) => pushDataLayerEvent({ ...dataInput, rx_config: rxConfigData })
      }
    )))
  );
};
export {
  RxGlassesConfigurator,
  RxGlassesConfigurator as default
};
//# sourceMappingURL=RxGlassesConfigurator.js.map
